import { apiStatus } from '@va/constants';
import { OPEN_MODAL, openModal } from '@va/dashboard/actions/ui';
import { BusinessInfoCompletion } from '@va/dashboard/components';
import { useBusinessInfo } from '@va/dashboard/shared/account-settings';
import { useTranslate } from '@va/localization';
import { START_CREATE_AGENCY_FLOW } from '@va/standalone/shared/actions';
import { modalName as standaloneModals } from '@va/standalone/shared/constants';
import {
  getApiRequestStatus,
  isAgencyAccount as isAgencyAccountSelector,
  isBusinessInfoCompleted as isBusinessInfoCompletedSelector,
} from '@va/standalone/shared/selectors';
import { Button, DetailsSection, NotificationsCount } from '@va/ui/design-system';
import { getBusinessSectorName } from '@va/util/helpers';
import { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InfoField from '../common/InfoField';

const BusinessDetails = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const isAgencyAccount = useSelector(isAgencyAccountSelector);
  const { businessInfo, getSelectedBusinessSize, getSelectedBusinessType } = useBusinessInfo();
  const agencyCustomerInfoApiStatus = useSelector((state) => getApiRequestStatus(state, 'agencyCustomerInfo'));
  const isBusinessInfoCompleted = useSelector(isBusinessInfoCompletedSelector);

  function handleSelectedWhiteLabelWebsite(websiteId: string) {
    dispatch({ type: START_CREATE_AGENCY_FLOW, websiteId: websiteId });
  }

  function handleAgencyFreePlanClick() {
    dispatch(
      openModal(standaloneModals.WHITE_LABEL_WEBSITE, {
        onSubmit: handleSelectedWhiteLabelWebsite,
      }),
    );
  }

  const isInProgress = useMemo(
    () => agencyCustomerInfoApiStatus === apiStatus.IN_PROGRESS,
    [agencyCustomerInfoApiStatus],
  );

  return (
    <DetailsSection
      title={translate('account.businessDetails.title')}
      sectionName={'businessDetails'}
      additionalClass='bg-gray-alabaster'
      onButtonClick={() =>
        dispatch({
          type: OPEN_MODAL,
          modalName: standaloneModals.BUSINESS_INFO,
        })
      }
      buttonProps={{
        ...(isBusinessInfoCompleted
          ? {}
          : {
              rightSideIcon: <NotificationsCount count={1} className='border-1.5 border-white/50' />,
            }),
      }}
      inProgress={isInProgress}
    >
      {!isInProgress && (
        <Fragment>
          <BusinessInfoCompletion className='mb-36px' businessInfo={businessInfo} />
          <div className='grid gap-8u lg:gap-20u grid-cols-1 md:grid-cols-2'>
            <InfoField
              className='md:col-span-2'
              label={translate('form.placeholders.businessName')}
              value={businessInfo?.companyName}
            />
            <InfoField label={translate('form.placeholders.firstName')} value={businessInfo?.firstName} />
            <InfoField label={translate('form.placeholders.lastName')} value={businessInfo?.lastName} />
            <InfoField label={translate('form.placeholders.companyWebsite')} value={businessInfo?.companyWebsite} />
            <InfoField label={translate('form.placeholders.vatIdentificationNumber')} value={businessInfo?.vat} />
            <InfoField
              label={translate('dropdown.businessData.businessType.placeholder')}
              value={getSelectedBusinessType(businessInfo?.businessType)?.label}
            />
            <InfoField
              label={translate('dropdown.businessData.businessSize.placeholder')}
              value={getSelectedBusinessSize(businessInfo?.businessSize)?.label}
            />
            <InfoField
              label={translate('dropdown.businessData.businessSector.placeholder')}
              value={translate(getBusinessSectorName(businessInfo?.businessSector) ?? '')}
            />
            <InfoField label={translate('form.placeholders.streetAndNumber')} value={businessInfo?.street} />
            <InfoField label={translate('form.placeholders.zipCode')} value={businessInfo?.zipcode} />
            <InfoField label={translate('form.placeholders.city')} value={businessInfo?.city} />
            <InfoField label={translate('form.placeholders.state')} value={businessInfo?.state} />
            <InfoField label={translate('form.placeholders.country')} value={businessInfo?.country} />
          </div>

          {!isAgencyAccount && (
            <Fragment>
              <div className='mt-4 md:mt-8'>{translate('account.businessDetails.agencyAccountInfo')}</div>
              <div className='my-3 '>
                <Button
                  onClick={handleAgencyFreePlanClick}
                  className={'!bg-orange-pure'}
                  text={translate('button.agencyWhiteLabel.tryFreePlan')}
                />
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
    </DetailsSection>
  );
};

export default BusinessDetails;
